import { env } from '../env'

const INSURANCE_PARTNER_DISPLAY = {
  authTrustBadgeEnabled: true,
} as const

export function isAuthInsurancePartnerTrustBadgeEnabled() {
  return INSURANCE_PARTNER_DISPLAY.authTrustBadgeEnabled
}

const COVERAGE_PUBLIC_CONFIG = {
  coverageStatus: env.coverageStatus,
  assistancePhonePrimary: env.coverageAssistancePhonePrimary,
  assistancePhoneSecondary: env.coverageAssistancePhoneSecondary,
} as const

export const COVERAGE_LIVE_PARTNER = {
  displayName: 'Qualitas',
  legalName: 'Qualitas Compania de Seguros',
  assistanceLabel: 'Cabina de asistencia',
  /**
   * El magenta de su marca, muestreado del PNG oficial — es el color dominante
   * del logo, no una aproximación.
   *
   * Vive acá y no en `lib/brand.ts` a propósito: `lib/brand.ts` es la paleta de
   * Tago, y este color no es nuestro. Se usa solo como filete del soporte donde
   * va su logo, nunca como acento de la interfaz: el coral sigue siendo el
   * único color que dirige la atención en nuestras pantallas.
   */
  brandColor: '#951B80',
  /**
   * El logo se presenta siempre sobre blanco, en los dos temas.
   *
   * Es un PNG con transparencia y su tipografía es turquesa: sobre el fondo
   * oscuro de la app mide cerca de 3:1 y se ensucia. Un soporte blanco lo
   * devuelve al fondo para el que fue diseñado, así que se ve igual en claro y
   * en oscuro — que es exactamente lo que una marca espera de quien la exhibe,
   * y más cuando su gente está en la sala.
   */
  plaqueColor: '#FFFFFF',
} as const

/**
 * Show the insurer's name and logo only when the insurer is actually on the
 * hook — i.e. only when policies are being issued.
 *
 * This used to read `!== 'coming_soon'`, while the edge layer issues only on
 * `=== 'live'` (`supabase/functions/_shared/coverage.ts`). The enum has three
 * values, so the two thresholds disagreed on exactly one of them: `pilot`
 * painted Quálitas's logo and legal name across checkout, the trip screen and
 * the coverage screen while the server issued nothing. A guest would have read
 * an insurer's name on the screen where they pay, backed by no policy.
 *
 * `pilot` is not implemented anywhere else either — the server has never known
 * the word. Until it means something on both sides, the honest reading of it is
 * the same as `coming_soon`, and `=== 'live'` gives that for free while keeping
 * this in lockstep with issuance instead of one value away from it.
 */
export function isCoveragePartnerBrandingVisible(): boolean {
  return COVERAGE_PUBLIC_CONFIG.coverageStatus === 'live'
}

/**
 * Qué se puede afirmar hoy sobre Quálitas, en una sola función.
 *
 * Son dos afirmaciones distintas y solo una depende de que existan pólizas:
 *
 * - `alliance` — "nuestro aliado asegurador es Quálitas". Es cierto desde que
 *   se firmó el contrato, y es cierto en una pantalla donde nadie paga ni viaja
 *   todavía. Acá el logo informa quién es el aliado; no promete una póliza.
 * - `per_trip` — "este viaje lleva póliza de Quálitas". Eso solo es cierto
 *   cuando el borde emite, y el borde emite únicamente en `live`.
 *
 * La distinción existe porque la portada y el checkout no son lo mismo. En el
 * checkout la marca del asegurador va junto al monto que alguien está a punto
 * de pagar, y ahí `isCoveragePartnerBrandingVisible()` la retiene hasta que hay
 * póliza detrás. En la portada la afirmación es de alianza, y esa ya se firmó.
 *
 * Lo que NO puede pasar es que la portada diga "cada viaje" antes de tiempo: por
 * eso `per_trip` cuelga del mismo `'live'` que emite, y no de un texto suelto.
 */
export type CoveragePartnerClaim = 'alliance' | 'per_trip'

export function getCoveragePartnerClaim(): CoveragePartnerClaim {
  return COVERAGE_PUBLIC_CONFIG.coverageStatus === 'live' ? 'per_trip' : 'alliance'
}

export function getCoverageAssistancePhones(): string[] {
  const phones = [
    COVERAGE_PUBLIC_CONFIG.assistancePhonePrimary,
    COVERAGE_PUBLIC_CONFIG.assistancePhoneSecondary,
  ]
    .map((phone) => phone.trim())
    .filter(Boolean)

  return [...new Set(phones)]
}
